import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { myOrders, clearErrors } from '../../actions/orderAction';
import Loader from '../Layouts/Loader';
import { useSnackbar } from 'notistack';
import OrderItem from './OrderItem';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import SearchIcon from '@mui/icons-material/Search';
import MetaData from '../Layouts/MetaData';
import { debounce } from 'lodash';

const orderStatus = ["Processing", "Shipped", "Delivered"];
const dt = new Date();
const ordertime = [dt.getMonth(), dt.getFullYear() - 1, dt.getFullYear() - 2];

const MyOrders = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [status, setStatus] = useState("");
    const [orderTime, setOrderTime] = useState(0);
    const [search, setSearch] = useState("");

    const { orders, loading, error } = useSelector((state) => state.myOrders);

    // Handle errors and dispatch fetch on mount
    useEffect(() => {
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            dispatch(clearErrors());
        }
        dispatch(myOrders());
    }, [dispatch, error, enqueueSnackbar]);

    // Debounced search handler
    const handleSearch = debounce((e) => {
        setSearch(e.target.value);
    }, 500); // Delay of 500ms

    const clearFilters = () => {
        setStatus("");
        setOrderTime(0);
    };

    // Memoize filtered orders to avoid unnecessary recomputations
    const filteredOrders = useMemo(() => {
        if (!orders) return [];

        return orders.filter((order) => {
            const matchesStatus = status ? order.orderStatus === status : true;
            const matchesOrderTime =
                orderTime === 0
                    ? true
                    : new Date(order.createdAt).getFullYear() === orderTime ||
                      new Date(order.createdAt).getMonth() === orderTime;

            return matchesStatus && matchesOrderTime;
        }).map((order) => ({
            ...order,
            orderItems: order.orderItems.filter((item) =>
                item.name.toLowerCase().includes(search.toLowerCase())
            ),
        }));
    }, [orders, status, orderTime, search]); // Recompute only when necessary

    return (
        <>
            <MetaData title="My Orders | Siddhi Creatives" />
            <main className="w-full mt-1 sm:mt-24">
                <div className="flex gap-3.5 mt-20 sm:mt-6 sm:mx-3 m-auto mb-7">
                    {/* Sidebar Column */}
                    <div className="hidden sm:flex flex-col w-1/5 px-1">
                        <div className="flex flex-col bg-white rounded-lg shadow-lg">
                            <div className="flex items-center justify-between gap-5 px-4 py-2 border-b">
                                <p className="text-lg font-semibold text-gray-800">Filters</p>
                                <span onClick={clearFilters} className="text-blue-600 font-medium text-sm uppercase cursor-pointer hover:text-blue-700">Clear All</span>
                            </div>

                            {/* Order Status Radio Buttons */}
                            <div className="flex flex-col py-3 text-sm">
                                <span className="font-medium px-4">ORDER STATUS</span>
                                <div className="flex flex-col gap-3 px-4 mt-1 pb-3 border-b">
                                    <FormControl>
                                        <RadioGroup
                                            aria-labelledby="orderstatus-radio-buttons-group"
                                            onChange={(e) => setStatus(e.target.value)}
                                            name="orderstatus-radio-buttons"
                                            value={status}
                                        >
                                            {orderStatus.map((el, i) => (
                                                <FormControlLabel
                                                    value={el}
                                                    control={<Radio size="small" />}
                                                    key={i}
                                                    label={<span className="text-sm">{el}</span>}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </div>

                            {/* Order Time Radio Buttons */}
                            <div className="flex flex-col pb-2 text-sm">
                                <span className="font-medium px-4">ORDER TIME</span>
                                <div className="flex flex-col gap-3 mt-1 px-4 pb-3">
                                    <FormControl>
                                        <RadioGroup
                                            aria-labelledby="ordertime-radio-buttons-group"
                                            onChange={(e) => setOrderTime(e.target.value)}
                                            name="ordertime-radio-buttons"
                                            value={orderTime}
                                        >
                                            {ordertime.map((el, i) => (
                                                <FormControlLabel
                                                    value={el}
                                                    control={<Radio size="small" />}
                                                    key={i}
                                                    label={<span className="text-sm">{i === 0 ? "This Month" : el}</span>}
                                                />
                                            ))}
                                        </RadioGroup>
                                    </FormControl>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Orders Column */}
                    <div className="flex-1">
                        {loading ? (
                            <Loader />
                        ) : (
                            <div className="flex flex-col gap-3 sm:mr-4 overflow-hidden">
                                {/* Search Bar */}
                                <form className="flex items-center justify-between mx-1 sm:mx-0 sm:w-10/12 bg-white border rounded-lg shadow hover:shadow-md transition-shadow duration-300">
                                    <input
                                        value={search}
                                        onChange={handleSearch}
                                        type="search"
                                        name="search"
                                        placeholder="Search your orders here"
                                        className="p-2 text-sm outline-none flex-1 rounded-l-lg"
                                    />
                                    <button type="submit" className="h-full text-sm px-1 sm:px-4 py-2.5 text-white bg-primary-blue hover:bg-blue-600 rounded-r-lg flex items-center gap-1 transition duration-200">
                                        <SearchIcon sx={{ fontSize: "22px" }} />
                                        Search
                                    </button>
                                </form>

                                {/* No Results Found */}
                                {filteredOrders.length === 0 && (
                                    <div className="flex items-center flex-col gap-2 p-8 bg-white shadow-md rounded-lg">
                                        <img draggable="false" src="https://rukminim1.flixcart.com/www/100/100/promos/23/08/2020/c5f14d2a-2431-4a36-b6cb-8b5b5e283d4f.png" alt="Empty Orders" />
                                        <span className="text-lg font-medium text-gray-700">Sorry, no results found</span>
                                        <p className="text-gray-500">Edit search or clear all filters</p>
                                    </div>
                                )}

                                {/* Order Items */}
                                {filteredOrders.reverse().map((order) => {
                                    const { _id, orderStatus, orderItems, createdAt, deliveredAt } = order;
                                    return orderItems.map((item, index) => (
                                        <OrderItem {...item} key={index} orderId={_id} orderStatus={orderStatus} createdAt={createdAt} deliveredAt={deliveredAt} />
                                    ));
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </>
    );
};

export default MyOrders;
